/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";

// Tax-notice features piggyback on the admin_announcements table using
// kind = 'tax_notice'. There should be at most one active tax-notice
// announcement at a time. Helpers below abstract that detail.

export type TaxNoticeRow = {
  id: string;
  title: string;
  body_md: string;
  effective_date: string | null; // yyyy-mm-dd
  published_at: string | null;
  expires_at: string | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
};

export type TaxNoticeSendRow = {
  id: string;
  effective_date: string;
  sent_by: string;
  sent_at: string;
  recipient_count: number;
  dry_run: boolean;
  notes: string | null;
};

const TAX_NOTICE_KIND = "tax_notice" as const;

function rowToTaxNotice(row: any): TaxNoticeRow {
  return {
    id: row.id,
    title: row.title,
    body_md: row.body_md ?? "",
    effective_date: row.effective_date ?? null,
    published_at: row.published_at ?? null,
    expires_at: row.expires_at ?? null,
    is_pinned: !!row.is_pinned,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** Get the currently visible tax notice for end users (logged-in only). */
export async function getActiveTaxNotice(): Promise<TaxNoticeRow | null> {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("admin_announcements")
    .select("*")
    .eq("kind", TAX_NOTICE_KIND as any)
    .not("published_at", "is", null)
    .lte("published_at", nowIso)
    .order("published_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  const row = (data ?? [])[0] as any;
  if (!row) return null;
  if (row.expires_at && new Date(row.expires_at).getTime() <= Date.now()) return null;
  return rowToTaxNotice(row);
}

/** Admin: get the latest tax-notice row regardless of published state. */
export async function getLatestTaxNoticeForAdmin(): Promise<TaxNoticeRow | null> {
  const { data, error } = await supabase
    .from("admin_announcements")
    .select("*")
    .eq("kind", TAX_NOTICE_KIND as any)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  const row = (data ?? [])[0] as any;
  return row ? rowToTaxNotice(row) : null;
}

export type TaxNoticeUpsertInput = {
  title: string;
  body_md: string;
  effective_date: string | null; // yyyy-mm-dd
  publish: boolean;
};

export async function upsertTaxNotice(
  input: TaxNoticeUpsertInput,
  userId: string,
  existingId?: string,
): Promise<TaxNoticeRow> {
  const publishedAt = input.publish ? new Date().toISOString() : null;
  if (existingId) {
    const { data, error } = await supabase
      .from("admin_announcements")
      .update({
        title: input.title.trim(),
        body_md: input.body_md,
        effective_date: input.effective_date,
        published_at: publishedAt,
      } as any)
      .eq("id", existingId)
      .select("*")
      .single();
    if (error) throw error;
    return rowToTaxNotice(data);
  }
  const { data, error } = await supabase
    .from("admin_announcements")
    .insert({
      created_by_user_id: userId,
      title: input.title.trim(),
      body_md: input.body_md,
      severity: "warning",
      audience: "all",
      audience_tier: null,
      is_pinned: true,
      published_at: publishedAt,
      expires_at: null,
      kind: TAX_NOTICE_KIND,
      effective_date: input.effective_date,
    } as any)
    .select("*")
    .single();
  if (error) throw error;
  return rowToTaxNotice(data);
}

export async function setTaxNoticePublished(
  id: string,
  publish: boolean,
): Promise<TaxNoticeRow> {
  const { data, error } = await supabase
    .from("admin_announcements")
    .update({ published_at: publish ? new Date().toISOString() : null } as any)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return rowToTaxNotice(data);
}

export async function listTaxNoticeSends(): Promise<TaxNoticeSendRow[]> {
  const { data, error } = await supabase
    .from("tax_notice_sends" as any)
    .select("*")
    .order("sent_at", { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []) as unknown as TaxNoticeSendRow[];
}

export type SendTaxNoticeResult = {
  recipientCount: number;
  enqueued?: number;
  failureCount?: number;
  failures?: { email: string; error: string }[];
  labelBreakdown?: Record<string, number>;
  dryRun: boolean;
  environment: string;
};

export async function invokeSendTaxNotice(
  effectiveDate: string,
  dryRun: boolean,
  environment: "sandbox" | "live",
): Promise<SendTaxNoticeResult> {
  const { data, error } = await supabase.functions.invoke("admin-send-tax-notice", {
    body: { effectiveDate, dryRun, environment },
  });
  if (error) throw error;
  return data as SendTaxNoticeResult;
}